import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { Check, Edit3 } from "lucide-react";

export default function Perfil() {
  const [user] = useAuthState(auth);
  const [userData, setUserData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setUserData(docSnap.data());
          setFormData(docSnap.data());
        }
      }
    };
    fetchUserData();
  }, [user]);

  const handleResetPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, user.email);
      setSuccessMessage("Te hemos enviado un correo para restablecer tu contraseña.");
      setErrorMessage("");
    } catch (error) {
      console.error(error);
      setErrorMessage("Error al enviar el correo de recuperación.");
      setSuccessMessage("");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (e) => {
    const options = Array.from(e.target.selectedOptions, option => option.value);
    setFormData((prev) => ({ ...prev, intereses: options }));
  };

  const handleSave = async () => {
    try {
      await updateDoc(doc(db, "users", user.uid), formData);
      setUserData(formData);
      setEditMode(false);
    } catch (err) {
      console.error("Error actualizando datos:", err);
    }
  };

  if (!user || !userData) return null;
  const esJuridica = userData.tipoUsuario === "juridica";

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-10 bg-white rounded-xl shadow-lg mt-10 space-y-6">
      <div className="flex items-center gap-6">
        {user.photoURL && !imageError ? (
          <img src={user.photoURL} alt="Foto de perfil" className="w-24 h-24 rounded-full object-cover border border-gray-300 shadow" onError={() => setImageError(true)} />
        ) : (
          <div className="w-24 h-24 rounded-full bg-yellow-600 text-white flex items-center justify-center text-2xl font-bold">
            {user.email.charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{userData.nombre}</h1>
          <p className="text-sm text-gray-500">{user.email}</p>
          <p className="text-xs text-gray-400 mt-1">UID: {user.uid}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="p-4 bg-gray-50 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-700 mb-2">Datos personales</h2>
          <div className="space-y-2">
            <InputField label="Nombre" name="nombre" value={formData.nombre} onChange={handleInputChange} editable={editMode} />
            <InputField label="Teléfono" name="telefono" value={formData.telefono} onChange={handleInputChange} editable={editMode} />
            <InputField label="Provincia" name="provincia" value={formData.provincia} onChange={handleInputChange} editable={editMode} />
            {esJuridica && (
              <>
                <InputField label="Empresa" name="empresa" value={formData.empresa} onChange={handleInputChange} editable={editMode} />
                <InputField label="CIF" name="cif" value={formData.cif} onChange={handleInputChange} editable={editMode} />
                <SelectField label="Sector" name="sector" value={formData.sector} onChange={handleInputChange} editable={editMode} options={["tecnologia", "salud", "finanzas", "educacion", "otros"]} />
                <InputField label="Cargo" name="cargo" value={formData.cargo} onChange={handleInputChange} editable={editMode} />
              </>
            )}
            <InputField label="LinkedIn" name="linkedin" value={formData.linkedin} onChange={handleInputChange} editable={editMode} />
            <p><strong>Registro:</strong> {new Date(user.metadata.creationTime).toLocaleString()}</p>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-700 mb-2">Preferencias</h2>
          <p><strong>Rol actual:</strong> <span className={`font-bold ${userData.role === "premium" ? "text-yellow-600" : "text-gray-500"}`}>{userData.role.toUpperCase()}</span></p>
          <SelectField label="Objetivo en la plataforma" name="objetivo" value={formData.objetivo} onChange={handleInputChange} editable={editMode} options={["comprar", "vender", "explorar"]} />
          <SelectField label="¿Experiencia en M&A?" name="experiencia" value={formData.experiencia} onChange={handleInputChange} editable={editMode} options={["si", "no"]} />
          <MultiSelectField label="Sectores de interés" name="intereses" value={formData.intereses} onChange={handleMultiSelectChange} editable={editMode} options={["tecnologia", "salud", "educacion", "retail", "industria", "otros"]} />
          <SelectField label="Tamaño de empresa deseado" name="tamanoEmpresa" value={formData.tamanoEmpresa} onChange={handleInputChange} editable={editMode} options={["-100k", "100k-500k", "500k-1m", "1m+"]} />
        </div>
      </div>

      <div className="p-4 bg-gray-100 rounded-lg">
        <h2 className="text-lg font-semibold text-gray-700 mb-2">Seguridad</h2>
        <p className="text-sm text-gray-600 mb-3">
          Puedes restablecer tu contraseña en cualquier momento. Recibirás un correo con las instrucciones.
        </p>
        <button
          onClick={handleResetPassword}
          className="bg-yellow-600 text-white px-5 py-2 rounded hover:bg-yellow-700 transition text-sm"
        >
          Restablecer contraseña
        </button>
      </div>

      <div className="flex justify-end gap-4">
        {editMode ? (
          <>
            <button onClick={handleSave} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm">Guardar cambios</button>
            <button onClick={() => { setFormData(userData); setEditMode(false); }} className="text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
          </>
        ) : (
          <button onClick={() => setEditMode(true)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"><Edit3 size={16} />Editar perfil</button>
        )}
      </div>

      {successMessage && <div className="bg-green-100 text-green-700 p-3 rounded border border-green-200">{successMessage}</div>}
      {errorMessage && <div className="bg-red-100 text-red-700 p-3 rounded border border-red-200">{errorMessage}</div>}
    </div>
  );
}

function InputField({ label, name, value, onChange, editable }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600">{label}:</label>
      {editable ? (
        <input name={name} value={value || ""} onChange={onChange} className="w-full border rounded p-2 mt-1 text-sm" />
      ) : (
        <p>{value || "-"}</p>
      )}
    </div>
  );
}

function SelectField({ label, name, value, onChange, editable, options }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mt-2">{label}:</label>
      {editable ? (
        <select name={name} value={value || ""} onChange={onChange} className="w-full border rounded p-2 mt-1 text-sm">
          <option value="">Selecciona una opción</option>
          {options.map((opt) => <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>)}
        </select>
      ) : (
        <p>{value || "-"}</p>
      )}
    </div>
  );
}

function MultiSelectField({ label, name, value, onChange, editable, options }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mt-2">{label}:</label>
      {editable ? (
        <select multiple name={name} value={value || []} onChange={onChange} className="w-full border rounded p-2 mt-1 text-sm h-32">
          {options.map((opt) => <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>)}
        </select>
      ) : (
        <p>{(value && value.length > 0) ? value.join(", ") : "-"}</p>
      )}
    </div>
  );
}
